import { Button } from "@/components/ui/button";
import { SortOrder } from "@/graphql/__generated__/graphql";
import { updatePaginationState, usePaginationState } from "@/store/paginationStore";
import { ArrowUpDownIcon, ArrowUpWideNarrowIcon, ArrowDownWideNarrowIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * Configuration interface for the generic SortDropdownFilter component
 */
interface SortDropdownConfig<T extends string, QueryVariables> {
  /** The pagination state key (e.g., "paginatedTariffs", "paginatedHolidays") */
  paginationStateKey: keyof PaginationState;
  /** The sort field enum values */
  sortFields: Record<string, T>;
  /** Function to get user-friendly names for sort fields */
  getSortFieldName: (_field: T) => string;
  /** Default sort field when no field is selected */
  defaultSortField: T;
  /** Function to build query variables with current filters and sort */
  buildQueryVariables: (_displayAmount: number, _filters: any, _sort: { field: T; order: SortOrder } | null) => QueryVariables;
  /** Function to extract filters from pagination state */
  extractFilters: (_paginationState: any) => any;
}

interface Props<T extends string, QueryVariables> {
  displayAmount: number;
  handleSubmitClick: (_variables: QueryVariables) => void;
  config: SortDropdownConfig<T, QueryVariables>;
}

/**
 * Generic SortDropdownFilter component
 * A reusable dropdown menu for sorting data by various fields and orders.
 * Can be configured for different configuration types (tariffs, holidays, etc.)
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - The number of items to display per page
 * @param {Function} props.handleSubmitClick - Callback invoked with updated query variables when sort changes
 * @param {SortDropdownConfig} props.config - Configuration object defining the behavior for this specific use case
 * @returns {JSX.Element} A dropdown menu for selecting sort field and order
 *
 * @example
 * // For tariffs
 * <SortDropdownFilter
 *   displayAmount={20}
 *   handleSubmitClick={handleTariffSortChange}
 *   config={tariffSortConfig}
 * />
 */
const SortDropdownFilter = <T extends string, QueryVariables>(props: Props<T, QueryVariables>) => {
  // Props
  const { displayAmount, handleSubmitClick, config } = props;

  // State
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);

  // Hooks
  const { t } = useTranslation(["translation", "common", "components"]);

  // Pagination
  const paginationState = usePaginationState(config.paginationStateKey);
  const { sort } = paginationState as any;
  const filters = config.extractFilters(paginationState);

  /**
   * Handles changing the sort field and order
   * @param {T | null} field - The field to sort by
   * @param {SortOrder | null} order - The order to sort by (ascending or descending)
   */
  const handleSortChange = (field: T | null, order: SortOrder | null) => {
    // Create the new sort value
    const newSort = field && order ? { field, order } : null;

    // Update pagination state with new sort values
    updatePaginationState({
      [config.paginationStateKey]: {
        sort: newSort
      }
    });

    // Build and submit query variables
    const baseVariables = config.buildQueryVariables(displayAmount, filters, newSort);

    // For sort changes, we need to explicitly include the sort value to avoid race conditions
    // with the pagination state update
    const updatedVariables = { ...baseVariables };
    if (newSort === null) {
      // Explicitly set sort to null when resetting
      (updatedVariables as any).sort = null;
    }

    handleSubmitClick(updatedVariables);
  };

  return (
    <DropdownMenu open={sortDropdownOpen} onOpenChange={setSortDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <ArrowUpDownIcon className="h-4 w-4" />
          {!sort?.field && (
            t("components:filters.sorting.sort")
          )}
          {sort && (
            <>
              {config.getSortFieldName(sort.field as T)}
              {sort.order === SortOrder.Asc ?
                <ArrowUpWideNarrowIcon className="h-4 w-4" />
                : <ArrowDownWideNarrowIcon className="h-4 w-4" />
              }
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[220px]">
        {/* Sort Order Section */}
        <div className="grid grid-cols-2 gap-1 p-1">
          <Button
            variant="ghost"
            size="sm"
            className={cn("justify-start col-span-1", sort?.order === SortOrder.Asc ? "bg-gray-100" : "hover:bg-accent/50")}
            onClick={() => {
              if (sort?.field) {
                handleSortChange(sort.field as T, SortOrder.Asc);
              } else if (sort?.order === SortOrder.Asc) {
                handleSortChange(null, null);
              } else {
                // Default to configured default field if no field is selected
                handleSortChange(config.defaultSortField, SortOrder.Asc);
              }
            }}
          >
            <ArrowUpWideNarrowIcon className="mr-2 h-4 w-4" />
            {t("components:filters.sorting.ascending")}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className={cn("justify-start col-span-1", sort?.order === SortOrder.Desc ? "bg-gray-100" : "hover:bg-accent/50")}
            onClick={() => {
              if (sort?.field) {
                handleSortChange(sort.field as T, SortOrder.Desc);
              } else if (sort?.order === SortOrder.Desc) {
                handleSortChange(null, null);
              } else {
                // Default to configured default field if no field is selected
                handleSortChange(config.defaultSortField, SortOrder.Desc);
              }
            }}
          >
            <ArrowDownWideNarrowIcon className="mr-2 h-4 w-4" />
            {t("components:filters.sorting.descending")}
          </Button>
        </div>

        {/* Sort Fields Section */}
        <DropdownMenuSeparator />
        {Object.values(config.sortFields).map((field) => (
          <DropdownMenuCheckboxItem
            key={field}
            className="rounded-md hover:cursor-pointer"
            checked={sort?.field === field}
            onCheckedChange={() => {
              if (sort?.order) {
                handleSortChange(field as T, sort.order as SortOrder);
              } else if (sort?.field === field) {
                handleSortChange(null, null);
              } else {
                // Default to ASC if no order is selected
                handleSortChange(field as T, SortOrder.Asc);
              }
            }}
            onSelect={(e) => e.preventDefault()}
          >
            {config.getSortFieldName(field as T)}
          </DropdownMenuCheckboxItem>
        ))}

        {/* Reset all sorting */}
        <div className="flex items-center gap-2 p-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              handleSortChange(null, null);
              setSortDropdownOpen(false);
            }}
          >
            {t("common:buttons.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SortDropdownFilter;
